import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SelectedAgentState } from "./selectedAgentTypes";
import { AgentListType } from "../../../pages/agent-management/AgentList/AgentTypes";

// Load from sessionStorage (per-tab: two tabs viewing different agents must not
// clobber each other; localStorage would be shared across tabs on the same origin)
const loadSelectedAgentFromStorage = (): AgentListType | null => {
  try {
    const saved = sessionStorage.getItem("selectedAgent");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Save to sessionStorage
const saveSelectedAgentToStorage = (agent: AgentListType | null) => {
  try {
    if (agent) {
      sessionStorage.setItem("selectedAgent", JSON.stringify(agent));
    } else {
      sessionStorage.removeItem("selectedAgent");
    }
  } catch (error) {
    console.error("Failed to save selected agent to sessionStorage:", error);
  }
};

// Load selectedParentAgent from sessionStorage
const loadSelectedParentAgentFromStorage = (): AgentListType | null => {
  try {
    const saved = sessionStorage.getItem("selectedParentAgent");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Save selectedParentAgent to sessionStorage
const saveSelectedParentAgentToStorage = (agent: AgentListType | null) => {
  try {
    if (agent) {
      sessionStorage.setItem("selectedParentAgent", JSON.stringify(agent));
    } else {
      sessionStorage.removeItem("selectedParentAgent");
    }
  } catch (error) {
    console.error("Failed to save selected parent agent to sessionStorage:", error);
  }
};

const initialState: SelectedAgentState = {
  selectedAgent: loadSelectedAgentFromStorage(),
  selectedParentAgent: loadSelectedParentAgentFromStorage(),
};

const selectedAgentSlice = createSlice({
  name: "selectedAgent",
  initialState,
  reducers: {
    setSelectedAgent: (state, action: PayloadAction<AgentListType | null>) => {
      state.selectedAgent = action.payload;
      saveSelectedAgentToStorage(action.payload);
    },
    clearSelectedAgent: (state) => {
      state.selectedAgent = null;
      sessionStorage.removeItem("selectedAgent");
    },
    setSelectedParentAgent: (state, action: PayloadAction<AgentListType | null>) => {
      state.selectedParentAgent = action.payload;
      saveSelectedParentAgentToStorage(action.payload);
    },
    clearSelectedParentAgent: (state) => {
      state.selectedParentAgent = null;
      sessionStorage.removeItem("selectedParentAgent");
    },
  },
});

export const { setSelectedAgent, clearSelectedAgent, setSelectedParentAgent, clearSelectedParentAgent } =
  selectedAgentSlice.actions;

export default selectedAgentSlice.reducer;
